import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Sparkles, Plus, Minus } from 'lucide-react';

/**
 * RoiCalculator — Interactive personalized ROI calculator for /app
 *
 * 2 simple inputs:
 *  - Commandes Uber/Deliveroo per month
 *  - Nombre de salariés
 *  - (Optional toggle) Tu as un manager ?
 *  - Nombre de restos
 *
 * Output: net monthly recovery, big animated number + breakdown
 *
 * Props:
 *  - basePrice (number, default 229)
 *  - pricePerExtraResto (number, default 99)
 *  - onSubscribe (function) — CTA handler
 */
const APP_BASE_PRICE = 229;
const APP_PER_EXTRA_RESTO = 99;

const Stepper = ({ value, onChange, min = 0, max = 999, step = 1, testid }) => (
  <div className="flex items-center gap-2 justify-center">
    <button
      type="button"
      onClick={() => onChange(Math.max(min, value - step))}
      className="w-10 h-10 rounded-full bg-white border border-[#1a1a1a]/15 flex items-center justify-center hover:bg-[#1a1a1a]/[0.04] transition-colors"
      data-testid={`${testid}-minus`}
      aria-label="Diminuer"
    >
      <Minus className="w-4 h-4 text-[#1a1a1a]" />
    </button>
    <div className="min-w-[70px] text-center">
      <span className="font-bebas text-3xl tabular-nums leading-none" style={{ color: '#F05A1A' }}>
        {value}
      </span>
    </div>
    <button
      type="button"
      onClick={() => onChange(Math.min(max, value + step))}
      className="w-10 h-10 rounded-full bg-white border border-[#1a1a1a]/15 flex items-center justify-center hover:bg-[#1a1a1a]/[0.04] transition-colors"
      data-testid={`${testid}-plus`}
      aria-label="Augmenter"
    >
      <Plus className="w-4 h-4 text-[#1a1a1a]" />
    </button>
  </div>
);

const RoiCalculator = ({ onSubscribe }) => {
  const [ordersPerMonth, setOrdersPerMonth] = useState(150);
  const [staffCount, setStaffCount] = useState(6);
  const [hasManager, setHasManager] = useState(true);
  const [restoCount, setRestoCount] = useState(1);

  // === ROI calculation ===
  const numbers = useMemo(() => {
    // 1. Litiges Uber/Deliveroo : ~0.75€ par commande récupérable en moyenne
    const litigesRecovered = Math.round(ordersPerMonth * 0.75);

    // 2. Économie formation/turnover : 40% turnover × 1500€/recrue / 12 mois
    const turnoverSaving = Math.round((staffCount * 0.4 * 1500) / 12);

    // 3. Salaire manager économisé : 2 000€/mois si l'utilisateur dit qu'il a un manager
    const managerSaving = hasManager ? 2000 : 0;

    // 4. Économies sur abonnements remplacés (POS, planning, RH, etc.)
    const subscriptionsSaving = 600;

    // 5. Food cost optimisé estimé : 8€/salarié/jour récupéré × 30j (proxy simple)
    const foodCostSaving = Math.round(staffCount * 8 * 30 * 0.4);

    // Investissement : base + (n-1) × extra
    const investment = APP_BASE_PRICE + Math.max(0, restoCount - 1) * APP_PER_EXTRA_RESTO;

    const grossRecovery = litigesRecovered + turnoverSaving + managerSaving + subscriptionsSaving + foodCostSaving;
    const netRecovery = grossRecovery - investment;

    return { litigesRecovered, turnoverSaving, managerSaving, subscriptionsSaving, foodCostSaving, investment, grossRecovery, netRecovery };
  }, [ordersPerMonth, staffCount, hasManager, restoCount]);

  return (
    <section
      className="py-14 sm:py-20 px-5 sm:px-8 border-t"
      style={{ borderColor: 'rgba(26,26,26,0.08)', background: 'linear-gradient(180deg, #1A0E0E 0%, #2A1414 100%)' }}
      data-testid="roi-calculator-section"
    >
      <div className="max-w-[1100px] mx-auto">
        <div className="text-center mb-10">
          <p className="text-[10px] sm:text-xs font-black tracking-[3px] uppercase mb-3" style={{ color: '#F05A1A' }}>
            ⚡ Simulateur ROI · Tes vrais chiffres
          </p>
          <h2 className="font-bebas leading-[0.95] tracking-tight" style={{ color: '#fff', fontSize: 'clamp(40px, 7vw, 80px)' }}>
            COMBIEN <span style={{ color: '#F05A1A' }}>AUDIT RESTO</span><br />
            VA TE RAPPORTER ?
          </h2>
          <p className="text-white/60 text-sm sm:text-base mt-4 max-w-md mx-auto">
            Bouge 4 curseurs. Tu vois ton gain net mensuel — en direct.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 lg:gap-7">
          {/* LEFT — INPUTS */}
          <div className="lg:col-span-2 rounded-3xl p-6 sm:p-7 bg-white/[0.04] border border-white/10 space-y-6">
            {/* Orders */}
            <div>
              <label className="block text-white text-sm font-bold mb-1">
                📦 Commandes Uber / Deliveroo
              </label>
              <p className="text-white/45 text-[11px] mb-3">Par mois — toutes plateformes confondues</p>
              <Stepper value={ordersPerMonth} onChange={setOrdersPerMonth} min={0} max={2000} step={25} testid="roi-orders" />
            </div>

            {/* Staff */}
            <div>
              <label className="block text-white text-sm font-bold mb-1">
                👥 Salariés dans ton resto
              </label>
              <p className="text-white/45 text-[11px] mb-3">Cuisine + salle + livraison</p>
              <Stepper value={staffCount} onChange={setStaffCount} min={1} max={50} step={1} testid="roi-staff" />
            </div>

            {/* Manager toggle */}
            <div>
              <label className="block text-white text-sm font-bold mb-1">
                👔 Tu as un manager ?
              </label>
              <p className="text-white/45 text-[11px] mb-3">Si oui, Audit Resto le remplace en partie (économie ≈ 2 000€/mois)</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setHasManager(true)}
                  data-testid="roi-manager-yes"
                  className="flex-1 py-2.5 rounded-xl text-sm font-bold transition-all"
                  style={{
                    backgroundColor: hasManager ? '#F05A1A' : 'rgba(255,255,255,0.06)',
                    color: hasManager ? '#fff' : 'rgba(255,255,255,0.55)',
                    border: hasManager ? 'none' : '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  Oui (2 000€/mois)
                </button>
                <button
                  type="button"
                  onClick={() => setHasManager(false)}
                  data-testid="roi-manager-no"
                  className="flex-1 py-2.5 rounded-xl text-sm font-bold transition-all"
                  style={{
                    backgroundColor: !hasManager ? '#F05A1A' : 'rgba(255,255,255,0.06)',
                    color: !hasManager ? '#fff' : 'rgba(255,255,255,0.55)',
                    border: !hasManager ? 'none' : '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  Non
                </button>
              </div>
            </div>

            {/* Resto count */}
            <div>
              <label className="block text-white text-sm font-bold mb-1">
                🏢 Nombre de restos
              </label>
              <p className="text-white/45 text-[11px] mb-3">+99€/mois par établissement supplémentaire</p>
              <Stepper value={restoCount} onChange={setRestoCount} min={1} max={20} step={1} testid="roi-restos" />
            </div>
          </div>

          {/* RIGHT — RESULT */}
          <div className="lg:col-span-3 rounded-3xl p-6 sm:p-8 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, rgba(34,197,94,0.12) 0%, rgba(34,197,94,0.04) 100%)', border: '1px solid rgba(34,197,94,0.25)' }}>
            <p className="text-green-400 text-[10px] sm:text-xs font-black tracking-[3px] uppercase mb-3">
              ⚡ Ton gain net estimé
            </p>

            {/* Net recovery — big animated number */}
            <div className="mb-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={numbers.netRecovery}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.3 }}
                  className="flex items-baseline gap-2 flex-wrap"
                >
                  <span
                    className="font-bebas tabular-nums leading-none"
                    style={{
                      color: numbers.netRecovery >= 0 ? '#22C55E' : '#EF4444',
                      fontSize: 'clamp(56px, 11vw, 110px)',
                      textShadow: numbers.netRecovery >= 0 ? '0 0 50px rgba(34,197,94,0.35)' : 'none',
                    }}
                    data-testid="roi-net-recovery"
                  >
                    {numbers.netRecovery >= 0 ? '+' : ''}{numbers.netRecovery.toLocaleString('fr-FR')}€
                  </span>
                  <span className="text-white/70 text-lg sm:text-2xl font-bold">/ mois net</span>
                </motion.div>
              </AnimatePresence>
              <p className="text-white/55 text-sm mt-2">
                Soit <strong className="text-white">{(numbers.netRecovery * 12).toLocaleString('fr-FR')}€</strong> sur 12 mois — après déduction de l'abonnement.
              </p>
            </div>

            {/* Breakdown */}
            <div className="space-y-2 mb-6">
              {[
                { ic: '📦', label: 'Litiges Uber / Deliveroo récupérés', val: numbers.litigesRecovered, positive: true },
                { ic: '🍳', label: 'Food cost optimisé', val: numbers.foodCostSaving, positive: true },
                { ic: '👥', label: 'Turnover évité (formation auto)', val: numbers.turnoverSaving, positive: true },
                ...(numbers.managerSaving > 0 ? [{ ic: '👔', label: 'Salaire manager remplacé', val: numbers.managerSaving, positive: true, highlight: true }] : []),
                { ic: '📉', label: 'Abonnements remplacés (POS, planning, RH…)', val: numbers.subscriptionsSaving, positive: true },
                { ic: '💳', label: `Audit Resto (${restoCount} resto${restoCount > 1 ? 's' : ''})`, val: numbers.investment, positive: false },
              ].map((row, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between gap-3 py-2 px-3 rounded-xl"
                  style={{
                    backgroundColor: row.highlight ? 'rgba(240,90,26,0.10)' : 'rgba(255,255,255,0.03)',
                    border: row.highlight ? '1px solid rgba(240,90,26,0.25)' : '1px solid rgba(255,255,255,0.05)',
                  }}
                >
                  <span className="flex items-center gap-2 text-[12px] sm:text-sm flex-1 min-w-0" style={{ color: 'rgba(255,255,255,0.8)' }}>
                    <span className="text-base flex-shrink-0">{row.ic}</span>
                    <span className="truncate">{row.label}</span>
                  </span>
                  <span
                    className="font-bebas text-lg sm:text-xl tabular-nums flex-shrink-0"
                    style={{ color: row.positive ? '#22C55E' : '#FFB78A' }}
                  >
                    {row.positive ? '+' : '−'}{row.val.toLocaleString('fr-FR')}€
                  </span>
                </div>
              ))}
            </div>

            {/* CTA */}
            {onSubscribe && (
              <button
                type="button"
                onClick={onSubscribe}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-2xl font-extrabold text-white text-sm sm:text-base bg-[#F05A1A] hover:bg-[#FF6A2A] transition-colors shadow-[0_8px_28px_rgba(240,90,26,0.4)]"
                data-testid="roi-cta-subscribe"
              >
                <Sparkles className="w-4 h-4" />
                Démarrer · récupérer {Math.max(0, numbers.netRecovery).toLocaleString('fr-FR')}€/mois
                <ArrowRight className="w-4 h-4" />
              </button>
            )}

            <p className="text-white/40 text-[11px] mt-3 italic leading-snug">
              Estimation basée sur les benchmarks observés sur nos clients existants. Tes résultats réels peuvent varier selon ton exécution.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RoiCalculator;
